<template>
  <div class="bg-black text-white">

    <main v-if="video" class="pt-0 md:pt-16">
      <!-- Hero Banner -->
      <section class="relative h-fit md:h-[70vh]">
        <div class="h-[200px] md:h-[60%] relative">
          <img :src="video.heroImage" :alt="video.title" class="w-full h-full object-cover" />
          <!-- The extra 1% is to ensure the gradient covers the image -->
          <div class="absolute h-[101%] inset-0 bg-gradient-to-t from-black to-transparent"></div>
        </div>
        
        <!-- Desktop Content Overlay (sm and up) -->
        <div class="hidden md:block absolute bottom-0 left-0 p-8 w-full">
          <h1 class="text-6xl font-semibold mb-4">{{ video.title }}</h1>
          <div class="flex items-center space-x-2 text-sm mb-4">
            <span>{{ formatDuration(video.content.duration) }}</span>
            <span>•</span>
            <span class="capitalize" v-for="genre in video.genres">{{ genre }}</span>
          </div>
          <p class="text-gray-300 max-w-2xl mb-6">{{ video.longDescription }}</p>
          
          <!-- Desktop Buttons -->
          <div class="flex flex-row items-center space-x-4">
            <button @click="playVideo"
              class="flex items-center space-x-2 px-8 py-3 bg-white text-black rounded-md hover:bg-gray-200 transition-colors">
              <font-awesome-icon icon="play" />
              <span class="font-semibold">PLAY {{ rentalMessage }}</span>
            </button>
            <RentalButton v-if="config.features.includes(FEATURES.REGISTRATION_RENTALS) && !video.is_rented && video.prices?.length"
              :video-title="video.title" :video-id="video.id" :rental-price="rentalPrice"
              @rental-success="handleRentalSuccess" />
            <!-- Socials Desktop -->
            <div class="flex items-center space-x-4 p-2 bg-gray-700 rounded-md"
              v-if="socials.facebook || socials.instagram">
              <span class="text-white text-xs font-medium">SHARE</span>
              <div class="flex items-center space-x-2">
                <!-- Facebook -->
                <button v-if="socials.facebook" @click="shareOnPlatform('facebook')"
                  class="flex items-center justify-center p-1 rounded-md bg-gray-800 opacity-80 text-white hover:bg-blue-600 transition-transform transform hover:scale-110">
                  <font-awesome-icon :icon="['fab', 'facebook']" size="xl" />
                </button>
                <!-- Instagram -->
                <button v-if="socials.instagram" @click="shareOnPlatform('instagram')"
                  class="flex items-center justify-center p-1 rounded-md bg-gray-800 opacity-80 text-white hover:bg-pink-500 transition-transform transform hover:scale-110">
                  <font-awesome-icon :icon="['fab', 'instagram']" size="xl" />
                </button>
              </div>
            </div>
            <!-- Favorite -->
            <button v-if="config.features.includes(FEATURES.REGISTRATION_FAVORITES)" 
              @click="toggleFav" class="p-2 rounded-full text-2xl focus:outline-none transition-colors"
              :class="isFav ? 'text-red-500' : 'text-white hover:text-red-500'" :aria-pressed="isFav"
              :title="isFav ? 'Remove from favorites' : 'Add to favorites'">
              <font-awesome-icon v-if='isFavLoading' icon="spinner" class="animate-spin text-white" />
              <font-awesome-icon v-else :icon="isFav ? ['fas', 'heart'] : ['far', 'heart']" />
            </button>
          </div>
        </div>

        <!-- Mobile Content Overlay (below sm) -->
        <div class="flex flex-col md:hidden mt-10 p-4 pb-8 w-full">
        
          <div class="py-2 space-x-2">
            <span class="capitalize text-xs bg-orange-600 px-3 py-1 rounded-full" v-for="genre in video.genres">{{ genre }}</span>
            <span v-if="video.releaseDate" class="capitalize text-xs bg-white bg-opacity-10 px-3 py-1 rounded-full">{{ new Date(video.releaseDate).getFullYear() }}</span>
            <span v-if="video.rating" class="capitalize text-xs bg-white bg-opacity-10 px-3 py-1 rounded-full">{{ video.rating.rating }} rated</span>
          </div>
          <div class="text-sm font-semibold my-2">{{ video.title }}</div>
          <div class="flex items-center space-x-2 text-xs mb-10">
            <span class="text-[var(--color-gray-350)]">{{ formatDurationMobile(video.content.duration) }}</span>
          </div>
          
          <!-- Mobile Buttons -->
          <div class="flex flex-col space-y-2 w-full">
            <!-- Favorite Button -->
            <button
              v-if="config.features.includes(FEATURES.REGISTRATION_FAVORITES)" 
              @click="toggleFav" 
              :class="[
                'w-full flex items-center justify-center h-10 space-x-2 px-8 py-3 text-white rounded-md transition-colors',
                isFav ? 'bg-orange-600 bg-opacity-100' : 'bg-white bg-opacity-10'
              ]"
              :aria-pressed="isFav"
              :title="isFav ? 'Remove from favorites' : 'Add to favorites'"
            >
              <font-awesome-icon v-if='isFavLoading' icon="spinner" class="animate-spin text-white" />
              <font-awesome-icon v-else :icon="isFav ? ['fas', 'heart'] : ['far', 'heart']" />
            </button>

            <!-- Watch Now Divider -->
            <div class="text-left text-white text-sm font-bold pb-2 pt-4">
              Watch Now
            </div>

            <!-- Rental Button -->
            <RentalButton v-if="config.features.includes(FEATURES.REGISTRATION_RENTALS) && !video.is_rented && video.prices?.length"
              :video-title="video.title" :video-id="video.id" :rental-price="rentalPrice"
              @rental-success="handleRentalSuccess"
              button-class="w-full bg-orange-600 h-10 text-white hover:bg-orange-700" />

            <!-- Play Button -->
            <button @click="playVideo"
              class="w-full flex flex-row items-center justify-between h-10 space-x-2 px-8 py-3 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-md transition-colors">
              <span class="font-semibold capitalize">Watch Free with Ads {{ rentalMessage }}</span>
              <font-awesome-icon icon="play" />
            </button>

             <!-- Socials Divider -->
            <div v-if="socials.facebook || socials.instagram" class="text-left text-white text-sm font-bold pb-2 pt-4">
              Share on
            </div>

            <div>
                <div class="flex items-center h-10 space-x-4"
                  v-if="socials.facebook || socials.instagram">
                    <!-- Facebook -->
                    <button v-if="socials.facebook" @click="shareOnPlatform('facebook')"
                      class="flex flex-1 h-10 items-center justify-center p-1 rounded-md bg-gray-700 opacity-80 text-white hover:bg-blue-600 transition-transform transform hover:scale-110">
                      <font-awesome-icon :icon="['fab', 'facebook']" size="xl" />
                    </button>
                    <!-- Instagram -->
                    <button v-if="socials.instagram" @click="shareOnPlatform('instagram')"
                      class="flex flex-1 h-10 items-center justify-center p-1 rounded-md bg-gray-700 opacity-80 text-white hover:bg-pink-500 transition-transform transform hover:scale-110">
                      <font-awesome-icon :icon="['fab', 'instagram']" size="xl" />
                    </button>
              
                </div>
            </div>
          </div>
          <div class="text-sm font-bold mb-2 pt-6">Synopsis</div>
          <p class="text-gray-300 mb-4 text-sm line-clamp-none md:line-clamp-2">{{ video.longDescription }}</p>
          
          <div class="text-xs text-[var(--color-gray-400)] mb-2 pt-6">Type</div>
          <div class=" text-white mb-8 text-sm md:line-clamp-1">Movie</div>
        </div>
      </section>
    </main>
  </div>
</template>


<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import { useVideoStore } from '@/store/videoStore'
import { useFavoritesStore } from '@/store/favoritesStore'
import { formatDuration, formatDurationMobile } from '@/utils/formatDuration'
import { useSocialShare } from '@/composables/useSocialShare'
import { generateSlug } from '@/utils/slugUtils'
import { useAdStore } from '@/store/adStore'
import { useConfigStore } from '@/store/configStore'
import { analyticsEvent } from '@/utils/analytics'
import { useAuth } from '@/composables/useAuth'
import { FEATURES } from '@/constants/features'
import { markUserInitiatedPlayerEntry } from '@/utils/playerAutoplayIntent'

import RentalButton from '@/components/RentalButton.vue'

const defaultFeedId = import.meta.env.VITE_DEFAULT_FEED_ID
const clienApiUrl = import.meta.env.VITE_CLIENT_API_URL

const favStore = useFavoritesStore()
const config = useConfigStore()
const route = useRoute()
const router = useRouter()
const videoStore = useVideoStore()
const adStore = useAdStore()
const video = ref(null)
const auth = useAuth()

const { fetchSocialUrls, shareOnPlatform, socialShareUrls } = useSocialShare()

const socials = ref({
  facebook: false,
  instagram: false
})

const rentalPrice = computed(() => {
  return video.value?.prices?.[0]?.amount ?? 0
})
const rentalMessage = computed(() => {
  const remainingDays = video.value?.rented?.daysUntilExpiration ?? "3"

  return video.value?.is_rented ? `WITHOUT ADS (${remainingDays} DAY${remainingDays === '1' ? '' : 'S'} LEFT)` : ''
})
const isFav = ref(null)
const requestedFav = ref(false)
const isFavLoading = ref(false)

watch([() => favStore.ids, video, requestedFav], () => {
  if (requestedFav.value && !favStore.ids.size)
    return

  if (!favStore.ids.size) {
    favStore.fetchServerFavorites(route.params.partnerId || defaultFeedId, auth.user.value?.app_metadata.mongo_id)
    requestedFav.value = true
  } else if (video.value) {
    isFav.value = favStore.isFavorited(video.value.id)
  }
}, { deep: true })

async function toggleFav() {
  if (!video.value) return
  isFavLoading.value = true

  if (!auth.isAuthenticated.value) {
    const slug = generateSlug(video.value.title, video.value.id)
    const returnTo = `/video/${slug}`
    await auth.login(returnTo)
  }

  const distributionId = route.params.partnerId || defaultFeedId
  const userId = auth.user.value.app_metadata.mongo_id

  try {
    const message = await favStore.toggle({ ...video.value, feedId: distributionId }, { userId, distributionId })
    isFav.value = favStore.isFavorited(video.value.id)
    toast.success(message)
  } catch (error) {
    console.error('Error toggling favorite:', error)
    toast.error('Error toggling favorite')
  } finally {
    isFavLoading.value = false
  }
}


function loadVideoBySlug(slug) {
  const allSingleVideos = videoStore.allSingleVideos
  video.value = allSingleVideos.find((v) => generateSlug(v.title, v.id) === slug)
  adStore.setupRequestMethod(adStore.requestMethods.GET)
  adStore.setupVideoToLookFor(video.value)
}

onMounted(async () => {
  const partnerId = route.params.partnerId
  const feedId = partnerId || defaultFeedId

  // If logged in, prefetch server favorites for this feed
  if (auth.user.value?.app_metadata.mongo_id && !favStore.favorites.length) {
    try {
      await favStore.fetchServerFavorites(feedId, auth.user.value?.app_metadata.mongo_id)
    } catch (error) {
      console.error('Error fetching server favorites:', error)
    }
  }

  await videoStore.loadVideos(feedId)
  let slug = route.params.slug
  // Redirects after payments encode the url and after decoding the + becomes a space
  if (slug.includes(' ')) {
    slug = slug.replace(/ /g, '+')
  }
  loadVideoBySlug(slug)

  if (!video.value) {
    router.push({ name: '404' })
  } else {
    await fetchSocialUrls(clienApiUrl, video.value.id, feedId)
    Object.keys(socialShareUrls.value || {}).forEach((platform) => {
      socials.value[platform] = Boolean(socialShareUrls.value[platform])
    })
  }
})

function playVideo() {
  analyticsEvent(video.value.title, "beacon", "play_clicked");

  const partnerId = route.params.partnerId
  const slug = generateSlug(video.value.title, video.value.id)
  markUserInitiatedPlayerEntry()
  router.push({ name: 'video-player', params: { partnerId, slug }, query: { ...route.query } });
}

async function handleRentalSuccess(rentalData) {
  // Track rental success event
  analyticsEvent(video.value.title, "beacon", "rental_success");

  console.log('Rental data:', rentalData);

  const { paymentIntent } = rentalData;
  if (paymentIntent?.status === 'succeeded') {

    // Give 3 seconds for the user to see the success message
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Mark the video as rented in the store
    videoStore.markVideoAsRented(video.value.id, rentalData);

    // Update the local video ref to reflect the rental status
    video.value.is_rented = true;

    video.value.rented = {
      ...video.value.rented,
      daysUntilExpiration: rentalData.daysUntilExpiration || "3",
      ...rentalData
    };
    console.log('Rental data:', rentalData)
    console.log('Video:', video.value)
    console.log('Is fav:', isFav.value)
    favStore.ids.add(video.value.id)
    console.log('Is fav:', isFav.value)
  }
}


onUnmounted(() => {
  adStore.setupRequestMethod(adStore.requestMethods.HEAD);
  adStore.setupVideoToLookFor(null);
})


</script>

<style scoped>
</style>
